import React, { Component } from "react";
import { get } from "../services/api";
import posthog from "posthog-js";
import { fetchUserCountry } from "../services/location";
import AthleteLink from "../components/AthleteLink";
import FilterControl from "../components/FilterControl";
import { WEIGHT_CLASS_CONFIG, getWeightClassFilterOptions } from "../components/Options";
import nationalities from "../data/nationalities.json";
import regions from "../data/regions.json";
import CircularProgress from '@mui/material/CircularProgress';

const KG_TO_LB = 2.20462;

const formatLiftValue = (value, unit = 'kg') => {
  if (value === null || value === undefined) return "";
  const num = Number(value) * (unit === 'lb' ? KG_TO_LB : 1);
  if (unit === 'lb') return Math.round(num);
  return Number.isInteger(num) ? num : num.toFixed(2);
};

export class Home extends Component {
  state = {
    isMobile: false,
    mobileFiltersOpen: false,
    loading: true,
    unfilteredLifts: [],
    weight_classFilter: undefined,
    genderFilter: undefined,
    nationalityFilter: undefined,
    lifts: [],
    sortBy: "total",
    next_page: undefined,
    unit: 'kg',
    userCountry: null,
  };

  componentDidMount = () => {
    posthog.capture("PageView", { route: "/" });

    const screenWidth = window.innerWidth;
    // const screenHeight = window.innerHeight;

    if (screenWidth < 500) {
      // console.log(screenWidth);
      this.setState({
        isMobile: true,
      });
    }
    // fetch lifts on page load
    this.getLiftsNew();

    // set filters
    const url = new URL(window.location);
    const gender = url.searchParams.get("gender");
    if (gender !== null && gender !== undefined) {
      this.setState({ genderFilter: gender });
    }
    const region = url.searchParams.get("region");
    if (region !== null && region !== undefined) {
      this.setState({ nationalityFilter: `region:${region}` });
    }
    const nationality = url.searchParams.get("nationality");
    if (nationality !== null && nationality !== undefined) {
      this.setState({ nationalityFilter: nationality });
    }
    const weight_class_limit = url.searchParams.get("weight_class_limit");
    if (weight_class_limit !== null && weight_class_limit !== undefined) {
      this.setState({ weight_classFilter: Number(weight_class_limit) });
    }

    const savedUnit = localStorage.getItem('unit-preference');
    if (savedUnit === 'kg' || savedUnit === 'lb') {
      this.setState({ unit: savedUnit });
    }

    fetchUserCountry().then(country => {
      this.setState({ userCountry: country });
    }).catch(() => {});
  };

  handleFilter = (newFilter, value) => {
    // set the filter value in state then update lifts
    this.setState({ [newFilter + "Filter"]: value }, () => {
      this.updateUrlParam(newFilter, value);
    });
  };

  handleNationalityFilter = (value) => {
    this.setState({ nationalityFilter: value }, () => {
      const url = new URL(window.location);
      if (value === "All") {
        url.searchParams.delete("nationality");
        url.searchParams.delete("region");
      } else if (value.startsWith("region:")) {
        url.searchParams.delete("nationality");
        url.searchParams.set("region", value.replace("region:", ""));
      } else {
        url.searchParams.delete("region");
        url.searchParams.set("nationality", value);
      }
      window.history.pushState(null, "", url.toString());
      this.getLiftsNew();
    });
  };

  handleSort = (_, sortBy) => {
    if (sortBy !== this.state.sortBy) {
      this.setState({ sortBy });
      this.updateUrlParam("sortBy", sortBy);
    }
  };

  getLiftsNew = (order_by) => {
    this.setState({ loading: true });
    const url = new URL(window.location);
    let params = url.searchParams.toString();
    if (order_by !== null && order_by !== undefined) {
      params += `order_by=${order_by}`;
    }
    return get(`api/lifts?${params}`)
      .then((res) => {
        // console.log(res.data.length)
        this.setState({
          lifts: res.data,
          loading: false,
          unfilteredLifts: res.data,
          next_page: res.pagination?.next_page ?? null,
        });
        return res.data;
      })
      .catch((err) => {
        // console.log(err);
        this.setState({
          loading: false,
          error: true,
        });
      });
  };

  updateUrlParam = (key, value) => {
    const url = new URL(window.location);
    if (value === "All") {
      url.searchParams.delete(key);
    } else {
      url.searchParams.set(key, value);
    }
    window.history.pushState(null, "", url.toString());
    this.getLiftsNew();
  };

  updateMultipleUrlParams = (updates) => {
    const url = new URL(window.location);
    for (const [key, value] of Object.entries(updates)) {
      if (value === "All" || value === null || value === undefined) {
        url.searchParams.delete(key);
      } else {
        url.searchParams.set(key, value);
      }
    }
    window.history.pushState(null, "", url.toString());
    this.getLiftsNew();
  };

  handleWeightClassFilter = (value) => {
    const menLimits = WEIGHT_CLASS_CONFIG.Men.filter(wc => wc.limit !== null).map(wc => wc.limit);
    const womenLimits = WEIGHT_CLASS_CONFIG.Women.filter(wc => wc.limit !== null).map(wc => wc.limit);
    const numVal = Number(value);

    let gender = null;
    let weightClassLimit = null;

    if (value === "Men Open") {
      gender = "Male";
    } else if (value === "Women Open") {
      gender = "Female";
    } else if (menLimits.includes(numVal)) {
      gender = "Male";
      weightClassLimit = numVal;
    } else if (womenLimits.includes(numVal)) {
      gender = "Female";
      weightClassLimit = numVal;
    }

    this.setState(
      {
        weight_classFilter: value,
        ...(gender !== null ? { genderFilter: gender } : {}),
      },
      () => {
        this.updateMultipleUrlParams({
          weight_class_limit: weightClassLimit ?? "All",
          ...(gender !== null ? { gender } : {}),
        });
      }
    );
  };

  handleUnitChange = (unit) => {
    localStorage.setItem('unit-preference', unit);
    this.setState({ unit });
  };

  loadMoreLifts = () => {
    const { next_page } = this.state;
    if (next_page === null || next_page === undefined) return;
    this.setState({ loading: true });

    const url = new URL(window.location);
    let params = url.searchParams.toString();
    if (params === "") {
      params = `page=${next_page}`;
    } else {
      params += `&page=${next_page}`;
    }

    get(`api/lifts?${params}`).then((res) => {
      if (res.data.length > 0) {
        this.setState({
          lifts: this.state.lifts.concat(res.data),
          next_page: res.pagination?.next_page ?? null,
          loading: false,
        });
      }
    });
  };

  render() {
    const {
      error,
      genderFilter,
      isMobile,
      mobileFiltersOpen,
      loading,
      lifts,
      nationalityFilter,
      weight_classFilter,
      next_page,
      unit,
    } = this.state;

    const { sortBy } = this.state;
    const filterControls = (
      <div className="bg-gray-200 px-4 py-3 sm:px-6">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
          <div className="flex flex-wrap items-end gap-3">
            <FilterControl
              label="Gender"
              controlType={isMobile ? "select" : "button-group"}
              value={genderFilter || "All"}
              options={[
                { label: "All", value: "All" },
                { label: "Male", value: "Male" },
                { label: "Female", value: "Female" },
              ]}
              onChange={(value) => this.handleFilter("gender", value)}
            />
            <FilterControl
              label="Weight Class"
              controlType="select"
              value={weight_classFilter || "All"}
              options={getWeightClassFilterOptions()}
              onChange={(value) => this.handleWeightClassFilter(value)}
            />
            <FilterControl
              label="Region/ Country"
              controlType="select"
              value={nationalityFilter || "All"}
              options={[
                { label: "All", value: "All" },
                ...regions.map((region) => ({
                  label: region,
                  value: `region:${region}`,
                })),
                ...nationalities.map((nation) => ({
                  label: nation,
                  value: nation,
                })),
              ]}
              onChange={(value) => this.handleNationalityFilter(value)}
            />
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <FilterControl
              label="Unit"
              controlType={isMobile ? "select" : "button-group"}
              value={unit}
              options={[
                { label: "kg", value: "kg" },
                { label: "lb", value: "lb" },
              ]}
              onChange={(value) => this.handleUnitChange(value)}
            />
            <FilterControl
              label="Lift Type"
              controlType={isMobile ? "select" : "button-group"}
              value={sortBy}
              options={[
                { label: "Total", value: "total" },
                { label: "Log", value: "log" },
                { label: "Deadlift", value: "deadlift" },
              ]}
              onChange={(value) => this.handleSort(null, value)}
            />
          </div>
        </div>
      </div>
    );

    return (
      <div
        style={{
          backgroundColor: lifts.length % 2 === 0 ? "#F9FAFB" : "#F3F4F6",
          height: "98vh",
          overflowY: "scroll",
          paddingBottom: isMobile
            ? mobileFiltersOpen
              ? "25rem"
              : "8rem"
            : undefined,
        }}
      >
        <div className="fixed bottom-12 left-0 right-0 z-40 md:static">
          <div className="md:hidden">
            <button
              type="button"
              className="w-full bg-black px-4 py-2 text-center text-sm font-semibold tracking-wide text-white"
              onClick={() =>
                this.setState({ mobileFiltersOpen: !mobileFiltersOpen })
              }
            >
              {mobileFiltersOpen ? "Hide Filters" : "Show Filters"}
            </button>
            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${mobileFiltersOpen ? "max-h-[26rem] opacity-100" : "max-h-0 opacity-0"}`}
            >
              {filterControls}
            </div>
          </div>
          <div className="hidden md:block">{filterControls}</div>
        </div>
        <>
          {error ? (
            <div className="w-full text-center">Failed to load lifts</div>
          ) : (
            <span></span>
          )}
        </>
        <div style={{ position: 'relative' }}>
          {loading && lifts.length > 0 && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(209, 213, 219, 0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
              }}
            >
              <CircularProgress />
            </div>
          )}
          {loading && lifts.length === 0 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '4rem 0',
              }}
            >
              <CircularProgress />
            </div>
          )}
          {lifts.length > 0 && (
            <>
              {isMobile ? (
                <>
                  <div
                    className="flex py-2 font-semibold"
                    style={{ backgroundColor: "#fff" }}
                  >
                    <div className="w-24 pl-2">#</div>
                    <div className="w-96">Name</div>
                    <div className="w-36">Cls.</div>
                    <div className="w-48 pl-2">Log</div>
                    <div className="w-48">DL</div>
                    {/* <div className="w-48">Gender</div> */}
                  </div>
                  {lifts.map(
                    (
                      { uid, name, weight_class, log, deadlift, gender, rank },
                      idx,
                    ) => (
                      <div
                        className="py-4 flex w-full"
                        style={{
                          backgroundColor:
                            idx % 2 !== 0 ? "#F9FAFB" : "#F3F4F6",
                          borderLeft: "3px solid transparent",
                          transition: "border-color 150ms",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.borderLeftColor = "#ef4444")}
                        onMouseLeave={(e) => (e.currentTarget.style.borderLeftColor = "transparent")}
                        key={uid + idx}
                      >
                        <div className="w-24 pl-2">{rank}</div>
                        <div className="w-96">
                          <AthleteLink name={name} uid={uid} />
                        </div>
                        <div className="w-36">{weight_class}</div>
                        <div className="w-48 pl-2">{formatLiftValue(log, unit)}</div>
                        <div className="w-48">{formatLiftValue(deadlift, unit)}</div>
                        {/* <div className="w-48">{gender === 'Male' ? 'M' : 'F'}</div> */}
                      </div>
                    ),
                  )}
                </>
              ) : (
                <>
                  <div
                    id="lifts"
                    className="flex py-2 font-semibold"
                    style={{ backgroundColor: "#fff" }}
                  >
                    <div className="w-24 pl-2 lg:ml-12">#</div>
                    <div className="w-80">Name</div>
                    {/* <div className="w-48">Date</div> */}
                    <div className="w-80">Country</div>
                    <div className="w-48">Class</div>
                    {/* <div className="w-104">Competition</div> */}
                    <div className="w-48 pl-2">Log</div>
                    <div className="w-48">Deadlift</div>
                    <div className="w-48">Total</div>
                    {/* <div className="w-48">Gender</div>
                        <div className="w-36">Age</div>
                        <div className="w-24">Verified</div>  */}
                  </div>
                  {lifts.map(
                    (
                      {
                        uid,
                        competition,
                        name,
                        weight_class,
                        nationality,
                        competition_date,
                        log,
                        deadlift,
                        total,
                        gender,
                        age,
                        rank,
                      },
                      idx,
                    ) => (
                      <div
                        className="py-2 flex w-full"
                        style={{
                          backgroundColor:
                            idx % 2 !== 0 ? "#F9FAFB" : "#F3F4F6",
                          borderLeft: "3px solid transparent",
                          transition: "border-color 150ms",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.borderLeftColor = "#ef4444")}
                        onMouseLeave={(e) => (e.currentTarget.style.borderLeftColor = "transparent")}
                        key={uid + idx}
                      >
                        <div className="w-24 pl-2 lg:ml-12">{rank}</div>
                        <div className="w-80">
                          <AthleteLink name={name} uid={uid} />
                        </div>
                        {/* <div className="w-48">{competition_date}</div> */}
                        <div className="w-80">{nationality}</div>
                        <div className="w-48">{weight_class}</div>
                        {/* <div className="w-104">{competition}</div> */}
                        <div className="w-48 pl-2">{formatLiftValue(log, unit)}</div>
                        <div className="w-48">{formatLiftValue(deadlift, unit)}</div>
                        <div className="w-48">{formatLiftValue(total, unit)}</div>

                        {/*
                          <div className="w-48"><Example idx={idx}/></div>
                          */}
                        {/*<div className="w-48">{gender}</div>
                          <div className="w-36">{age}</div>*/}
                      </div>
                    ),
                  )}
                </>
              )}
            </>
          )}
        </div>
        <div className="w-full flex bg-white">
          <div className="w-1/3"></div>
          <div className="w-1/3 font-semibold text-center">
            {next_page !== null && next_page !== undefined ? (
              <button
                onClick={() => {
                  this.loadMoreLifts();
                }}
                className="bg-transparent hover:bg-black-500 text-black-700 font-semibold my-2 py-2 px-4 border border-black-500 hover:border-transparent rounded"
              >
                Load more
              </button>
            ) : (
              <div></div>
            )}
          </div>
          <div className="w-1/3"></div>
        </div>
        <div className="w-full flex bg-white">
          <a
            className="mx-auto text-center bg-white hover:text-blue-400 text-blue-300 font-semibold my-2 py-2 px-4"
            href="https://docs.google.com/forms/d/1kwRoqQDwOepLo1tF2VZZft6TAAqAlfaPULGk2lhnAjs/prefill"
            target="_blank"
            rel="noreferrer"
          >
            Leave Feedback
          </a>
        </div>
      </div>
    );
  }
}
